import { useMemo, useState, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { formatHours } from '@/utils/formatDayRange';
import type { CostCentre, Project, TimesheetEntry } from '@/types/timesheet';

type CopyMode = 'all' | 'billable' | 'selected';

interface EntryWithMeta {
  entry: TimesheetEntry;
  projectName?: string;
  projectCode?: string;
  costCentreName?: string;
  isBillable: boolean;
  totalHours: number;
}

interface CopyLastWeekDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Native entries from the previous week (schedule/integration/absence rows already excluded). */
  sourceEntries: TimesheetEntry[];
  projects: Project[];
  costCentres: CostCentre[];
  /** When true the dialog opens with only billable entries pre-selected. */
  defaultBillableOnly?: boolean;
  onConfirm: (entries: TimesheetEntry[]) => void;
}

const sumHours = (entry: TimesheetEntry): number => {
  const h = entry.hours;
  return h.monday + h.tuesday + h.wednesday + h.thursday + h.friday + h.saturday + h.sunday;
};

export function CopyLastWeekDialog({
  open,
  onOpenChange,
  sourceEntries,
  projects,
  costCentres,
  defaultBillableOnly = false,
  onConfirm,
}: CopyLastWeekDialogProps) {
  const enrichedEntries = useMemo<EntryWithMeta[]>(() => {
    return sourceEntries.map((entry) => {
      const project = projects.find((p) => p.id === entry.projectId);
      const costCentre = costCentres.find((c) => c.id === entry.costCentreId);
      return {
        entry,
        projectName: project?.name ?? entry.projectName,
        projectCode: project?.code ?? entry.projectCode,
        costCentreName: costCentre?.name ?? entry.costCentreName,
        isBillable: project?.billable ?? false,
        totalHours: sumHours(entry),
      };
    });
  }, [sourceEntries, projects, costCentres]);

  const billableCount = useMemo(
    () => enrichedEntries.filter((e) => e.isBillable).length,
    [enrichedEntries],
  );

  const [copyMode, setCopyMode] = useState<CopyMode>('all');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Reset selection whenever the dialog is (re)opened or the source entries change.
  useEffect(() => {
    if (!open) return;
    if (defaultBillableOnly) {
      setCopyMode('billable');
      setSelectedIds(new Set(enrichedEntries.filter((e) => e.isBillable).map((e) => e.entry.id)));
    } else {
      setCopyMode('all');
      setSelectedIds(new Set(enrichedEntries.map((e) => e.entry.id)));
    }
  }, [open, enrichedEntries, defaultBillableOnly]);

  const selectAll = () => setSelectedIds(new Set(enrichedEntries.map((e) => e.entry.id)));
  const selectBillableOnly = () =>
    setSelectedIds(new Set(enrichedEntries.filter((e) => e.isBillable).map((e) => e.entry.id)));

  const toggleEntry = (id: number) => {
    setCopyMode('selected');
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedTotalHours = useMemo(
    () =>
      enrichedEntries
        .filter((e) => selectedIds.has(e.entry.id))
        .reduce((sum, e) => sum + e.totalHours, 0),
    [enrichedEntries, selectedIds],
  );

  const handleConfirm = () => {
    const selected = enrichedEntries
      .filter((e) => selectedIds.has(e.entry.id))
      .map((e) => e.entry);
    onConfirm(selected);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-4 py-3 border-b text-left space-y-0">
          <DialogTitle>Copy Last Week</DialogTitle>
          <DialogDescription className="text-xs">
            Select which entries to copy. Hours are reset — only the entry structure is copied.
          </DialogDescription>
        </DialogHeader>

        {/* Mode selector */}
        <div className="px-4 py-2.5 border-b bg-muted/40">
          <div className="flex gap-1.5">
            <Button
              type="button"
              size="sm"
              variant={copyMode === 'all' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => {
                setCopyMode('all');
                selectAll();
              }}
            >
              All ({enrichedEntries.length})
            </Button>
            <Button
              type="button"
              size="sm"
              variant={copyMode === 'billable' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => {
                setCopyMode('billable');
                selectBillableOnly();
              }}
            >
              Billable ({billableCount})
            </Button>
            <Button
              type="button"
              size="sm"
              variant={copyMode === 'selected' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => setCopyMode('selected')}
            >
              Selected ({selectedIds.size})
            </Button>
          </div>
        </div>

        {/* Entry list */}
        <ScrollArea className="max-h-[45vh]">
          <div className="p-3 space-y-2">
            {enrichedEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Copy className="h-10 w-10 text-muted-foreground/40 mb-3" />
                <p className="text-sm font-medium">No Entries Found</p>
                <p className="text-xs text-muted-foreground">
                  There are no entries in the previous week to copy
                </p>
              </div>
            ) : (
              enrichedEntries.map(({ entry, projectName, projectCode, costCentreName, isBillable, totalHours }) => {
                const isSelected = selectedIds.has(entry.id);
                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => toggleEntry(entry.id)}
                    className={cn(
                      'w-full rounded-lg border p-3 text-left transition-colors',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-background hover:bg-muted/50',
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      <div
                        className={cn(
                          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors',
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-muted-foreground/40 bg-background',
                        )}
                      >
                        {isSelected && <Check className="h-3.5 w-3.5" />}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="truncate text-sm font-medium">
                                {projectName || 'Unknown Project'}
                              </span>
                              {isBillable && (
                                <Badge variant="success" className="shrink-0 px-1.5 py-0 text-[10px]">
                                  Billable
                                </Badge>
                              )}
                            </div>
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              {projectCode && <span className="font-mono">{projectCode}</span>}
                              {costCentreName && <span className="ml-1.5">• {costCentreName}</span>}
                            </div>
                          </div>
                          <div className="shrink-0 text-sm font-semibold">{formatHours(totalHours)}</div>
                        </div>
                        {entry.description && (
                          <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{entry.description}</p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-row items-center justify-between gap-2 border-t bg-muted/40 px-4 py-3 sm:justify-between">
          <div className="text-xs text-muted-foreground">
            Total: <span className="font-semibold text-foreground">{formatHours(selectedTotalHours)}</span>
            <span className="ml-2">
              {selectedIds.size} {selectedIds.size === 1 ? 'entry' : 'entries'}
            </span>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleConfirm} disabled={selectedIds.size === 0}>
              Copy {selectedIds.size} {selectedIds.size === 1 ? 'Entry' : 'Entries'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
